// ── BCHNostr — Optional at-rest key encryption (PIN / password) ────
//
// By default the Nostr secret key (nsec) and the tip-wallet WIF live in
// plaintext in localStorage. That's a hot-wallet trade-off: convenient, but
// anyone (or any script) that can read localStorage gets the keys. This
// module lets a user OPT IN to encrypting those secrets at rest behind a
// PIN / password, so a stolen localStorage dump is useless without it.
//
// Crypto: PBKDF2(SHA-256) key-stretches the PIN into an AES-GCM key. Each
// secret is sealed with a fresh random salt + IV. All primitives are the
// browser's own WebCrypto (SubtleCrypto) — no third-party crypto, nothing
// leaves the device, and the PIN itself is never stored anywhere.
//
// THREAT MODEL — what this does and does NOT do:
//   · Protects against a passive localStorage read / device handoff / backup
//     export: the ciphertext is meaningless without the PIN.
//   · Does NOT defend against a keylogger or an ACTIVE XSS payload that runs
//     while the key is unlocked in memory — once decrypted for use, the key
//     is in memory like any hot wallet. Encryption at rest raises the bar; it
//     is not a substitute for a hardware signer or NIP-07 extension.
//
// FORGOTTEN PIN = UNRECOVERABLE LOCALLY. There is no backdoor. The nsec can
// still be recovered from the user's own written-down backup, and the tip
// wallet from its WIF backup — the UI must make this consequence explicit
// before enabling.

const PBKDF2_ITERATIONS = 250_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;

/** Marker prefixing an encrypted envelope, so we can tell it apart from a
 *  legacy plaintext value at a glance (and refuse to treat one as the other). */
const ENVELOPE_PREFIX = 'bchenc:v1:';

interface Envelope {
  /** base64 PBKDF2 salt */
  s: string;
  /** base64 AES-GCM IV */
  i: string;
  /** base64 ciphertext (includes GCM tag) */
  c: string;
  /** iteration count, stored so we can raise it later without breaking old data */
  n: number;
}

// ── base64 helpers (Uint8Array ⇄ string), avoiding spread on large buffers ─

function toB64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function fromB64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ── Key derivation ────────────────────────────────────────────────

async function deriveKey(
  pin: string, salt: Uint8Array, iterations: number,
): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    'raw', enc.encode(pin), 'PBKDF2', false, ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ── Public API ────────────────────────────────────────────────────

/** True when the given stored value is one of our encrypted envelopes. */
export function isEncryptedValue(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.startsWith(ENVELOPE_PREFIX);
}

/**
 * Encrypt a secret string behind a PIN. Returns a self-describing envelope
 * string (safe to persist in localStorage) that `decryptSecret` can reverse.
 */
export async function encryptSecret(plaintext: string, pin: string): Promise<string> {
  if (!isCryptoAvailable()) throw new Error('Secure encryption is unavailable in this browser.');
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const key = await deriveKey(pin, salt, PBKDF2_ITERATIONS);
  const enc = new TextEncoder();
  const cipher = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plaintext)),
  );
  const envelope: Envelope = {
    s: toB64(salt),
    i: toB64(iv),
    c: toB64(cipher),
    n: PBKDF2_ITERATIONS,
  };
  return ENVELOPE_PREFIX + JSON.stringify(envelope);
}

/**
 * Decrypt an envelope with the PIN. Throws a friendly error on a wrong PIN
 * (AES-GCM authentication fails) or a malformed envelope.
 */
export async function decryptSecret(envelopeStr: string, pin: string): Promise<string> {
  if (!isEncryptedValue(envelopeStr)) {
    // Not encrypted — return as-is so callers can treat legacy plaintext
    // transparently.
    return envelopeStr;
  }
  let envelope: Envelope;
  try {
    envelope = JSON.parse(envelopeStr.slice(ENVELOPE_PREFIX.length)) as Envelope;
  } catch {
    throw new Error('Corrupted encrypted key.');
  }
  const salt = fromB64(envelope.s);
  const iv = fromB64(envelope.i);
  const cipher = fromB64(envelope.c);
  const key = await deriveKey(pin, salt, envelope.n || PBKDF2_ITERATIONS);
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
    return new TextDecoder().decode(plain);
  } catch {
    // AES-GCM tag mismatch — almost always the wrong PIN.
    throw new Error('Incorrect PIN.');
  }
}

/** Is WebCrypto with the primitives we need present? */
export function isCryptoAvailable(): boolean {
  return typeof crypto !== 'undefined'
    && !!crypto.subtle
    && typeof crypto.getRandomValues === 'function';
}

/**
 * Basic PIN policy. Returns an error string, or null when acceptable.
 * Intentionally lenient (a 4-digit PIN is allowed) but blocks the truly
 * trivial. Encryption strength comes from PBKDF2, not PIN length alone, but
 * a longer passphrase is always meaningfully stronger.
 */
export function validatePin(pin: string): string | null {
  if (!pin || pin.length < 4) return 'Use at least 4 characters.';
  if (pin.length > 256) return 'That is too long.';
  return null;
}
